<script setup>
// Preserve the existing public request-close event contract.
/* eslint-disable vue/custom-event-name-casing */
import { ref, computed } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';

import Button from 'dashboard/components-next/button/Button.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

const props = defineProps({
  contentTag: {
    type: String,
    default: 'form',
    validator: value => ['form', 'div'].includes(value),
  },
  type: {
    type: String,
    default: 'edit',
    validator: value => ['alert', 'edit'].includes(value),
  },
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  cancelButtonLabel: {
    type: String,
    default: '',
  },
  confirmButtonLabel: {
    type: String,
    default: '',
  },
  disableConfirmButton: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  showCancelButton: {
    type: Boolean,
    default: true,
  },
  showConfirmButton: {
    type: Boolean,
    default: true,
  },
  overflowYAuto: {
    type: Boolean,
    default: false,
  },
  width: {
    type: String,
    default: 'lg',
    validator: value =>
      ['6xl', '5xl', '4xl', '3xl', '2xl', 'xl', 'lg', 'md', 'sm'].includes(
        value
      ),
  },
  position: {
    type: String,
    default: 'center',
    validator: value => ['center', 'top'].includes(value),
  },
  // Quando true, clicar fora / ESC NÃO fecha direto: emite 'request-close' pro pai
  // decidir (ex.: confirmar antes de descartar um formulário preenchido).
  confirmBeforeClose: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['confirm', 'close', 'request-close']);

const { t } = useI18n();
const { isWorkspaceV2 } = useWorkspaceV2();

const dialogRef = ref(null);
const dialogContentRef = ref(null);
const isOpen = ref(false);

const maxWidthClass = computed(() => {
  const classesMap = {
    // O formulário único de campanha (4 blocos + resumo) pede uma folha larga:
    // sem 4xl/5xl/6xl o `width` caía no fallback max-w-md e a página inteira
    // ficava espremida em 448px, com uma palavra por linha.
    '6xl': 'max-w-6xl',
    '5xl': 'max-w-5xl',
    '4xl': 'max-w-4xl',
    '3xl': 'max-w-3xl',
    '2xl': 'max-w-2xl',
    // Modal padrão do guia Visor: até 552px.
    xl: 'max-w-[552px]',
    lg: 'max-w-lg',
    md: 'max-w-md',
    sm: 'max-w-sm',
  };

  return classesMap[props.width] ?? 'max-w-md';
});

const positionClass = computed(() =>
  props.position === 'top' ? 'dialog-position-top' : ''
);

const open = () => {
  isOpen.value = true;
  dialogRef.value?.showModal();
};
const close = () => {
  emit('close');
  dialogRef.value?.close();
  isOpen.value = false;
};

const requestClose = () => {
  if (props.confirmBeforeClose) emit('request-close');
  else close();
};

// Only close if the close event originated from this dialog,
// not from a child dialog (e.g. ProseMirror prompt) bubbling up.
const handleDialogClose = e => {
  // Native close events are queued and may arrive after another open().
  if (e.target === dialogRef.value && !e.target.open) close();
};

// Only close on click-outside if this dialog is the topmost one.
// If another dialog (e.g. ProseMirror prompt) is open on top, ignore.
const handleClickOutside = () => {
  const dialogs = document.querySelectorAll('dialog[open]');
  if (dialogs[dialogs.length - 1] !== dialogRef.value) return;
  if (props.confirmBeforeClose) {
    emit('request-close');
    return;
  }
  close();
};
// ESC: só intercepta quando confirmBeforeClose está ativo (pede confirmação ao pai).
// Sem a flag, não previne nada — o <dialog> fecha nativamente, como antes.
const handleEscape = e => {
  if (props.confirmBeforeClose) {
    e.preventDefault();
    emit('request-close');
  }
};
const handleEscapeKeydown = e => {
  if (
    !props.confirmBeforeClose ||
    e.target.closest('dialog') !== dialogRef.value
  )
    return;
  e.preventDefault();
  e.stopPropagation();
  emit('request-close');
};
const handleTab = event => {
  const dialog = dialogRef.value;
  if (event.target.closest('dialog') !== dialog) return;
  const controls = Array.from(
    dialog.querySelectorAll(
      'button, a[href], input, select, textarea, summary, [tabindex], [contenteditable="true"]'
    )
  ).filter(
    element =>
      !element.disabled &&
      (element.tabIndex >= 0 || element.isContentEditable) &&
      element.getClientRects().length &&
      element.closest('dialog') === dialog
  );
  const first = controls[0];
  const last = controls[controls.length - 1];
  if (!first) {
    event.preventDefault();
    return;
  }
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

const confirm = () => {
  emit('confirm');
};

defineExpose({ open, close });
</script>

<template>
  <TeleportWithDirection to="body">
    <dialog
      ref="dialogRef"
      :aria-label="title || undefined"
      class="w-full shadow-xl rounded-[var(--workspace-radius,20px)]"
      :class="[
        maxWidthClass,
        {
          '!w-[calc(100%_-_2rem)] !max-h-[calc(100dvh_-_2rem)] !overflow-y-auto overscroll-contain bg-n-background':
            isWorkspaceV2,
        },
        positionClass,
        overflowYAuto ? 'overflow-y-auto' : 'overflow-visible',
      ]"
      @close.prevent="handleDialogClose"
      @cancel="handleEscape"
      @keydown.esc="handleEscapeKeydown"
      @keydown.tab="handleTab"
    >
      <OnClickOutside @trigger="handleClickOutside">
        <component
          :is="contentTag"
          ref="dialogContentRef"
          class="flex flex-col w-full h-auto gap-6 p-6 overflow-visible text-start align-middle bg-n-alpha-3 backdrop-blur-[100px] shadow-xl rounded-[var(--workspace-radius,20px)]"
          @submit.prevent="confirm"
          @click.stop
        >
          <div v-if="title || description" class="flex flex-col gap-2">
            <h3 class="text-base font-medium leading-6 text-n-slate-12">
              {{ title }}
            </h3>
            <slot name="description">
              <p v-if="description" class="mb-0 text-sm text-n-slate-11">
                {{ description }}
              </p>
            </slot>
          </div>
          <slot v-if="isOpen" />
          <!-- Dialog content will be injected here -->
          <slot name="footer">
            <div
              v-if="showCancelButton || showConfirmButton"
              class="flex items-center justify-between w-full gap-3"
              :class="{ 'flex-wrap': isWorkspaceV2 }"
            >
              <Button
                v-if="showCancelButton"
                variant="faded"
                color="slate"
                :label="cancelButtonLabel || t('DIALOG.BUTTONS.CANCEL')"
                class="w-full"
                :class="{ 'min-h-11 min-w-44 !w-auto flex-1': isWorkspaceV2 }"
                type="button"
                @click="requestClose"
              />
              <Button
                v-if="showConfirmButton"
                :color="type === 'edit' ? 'blue' : 'ruby'"
                :label="confirmButtonLabel || t('DIALOG.BUTTONS.CONFIRM')"
                class="w-full"
                :class="{ 'min-h-11 min-w-44 !w-auto flex-1': isWorkspaceV2 }"
                :is-loading="isLoading"
                :disabled="disableConfirmButton || isLoading"
                type="submit"
              />
            </div>
          </slot>
        </component>
      </OnClickOutside>
    </dialog>
  </TeleportWithDirection>
</template>

<style scoped>
dialog::backdrop {
  @apply bg-n-alpha-black1 backdrop-blur-[4px];
}

.dialog-position-top {
  margin-top: clamp(2rem, 5vh, 5rem);
  margin-bottom: auto;
}
</style>

<style scoped>
/* SiteUp: <dialog> nativo sai de display:none; sem @starting-style a
   transition declarada nunca rodava. Guia Visor: entra em 200 ms a partir
   de scale .96, só opacity e transform. */
dialog[open] {
  opacity: 1;
  transform: scale(1);
  transition:
    opacity var(--motion-duration-modal) var(--motion-ease-enter),
    transform var(--motion-duration-modal) var(--motion-ease-enter),
    overlay var(--motion-duration-modal) allow-discrete,
    display var(--motion-duration-modal) allow-discrete;
}
@starting-style {
  dialog[open] {
    opacity: 0;
    transform: scale(0.96);
  }
}
dialog::backdrop {
  background: rgba(var(--modal-overlay));
  transition: opacity var(--motion-duration-modal) var(--motion-ease-enter);
}
@starting-style {
  dialog[open]::backdrop {
    opacity: 0;
  }
}
</style>
